/**
 * Typed client for the Products + Selling rules routes (productsLawsRoutes in @bs/contracts).
 * Same rules as api/client.ts: cookie session, CSRF header on mutations, ErrorEnvelope -> ApiError,
 * response checked against the contract. Also the "which business area" hook both pages share.
 */
import type { z } from 'zod';
import { API_PREFIX, ErrorEnvelope, productsLawsRoutes, type ProductsLawsRouteName } from '@bs/contracts';
import { api, UNAUTH_EVENT } from '../../api/instance.js';
import { ApiError, buildPath } from '../../api/client.js';

type R = typeof productsLawsRoutes;
type Out<K extends ProductsLawsRouteName> = z.output<R[K]['response']>;
type BodyIn<K extends ProductsLawsRouteName> = R[K] extends { body: z.ZodTypeAny } ? z.input<R[K]['body']> : undefined;

export interface PlCallOptions<K extends ProductsLawsRouteName> {
  params?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined>;
  body?: BodyIn<K>;
}

const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

export async function send(method: string, path: string, opts: { query?: PlCallOptions<ProductsLawsRouteName>['query']; body?: unknown }): Promise<Response> {
  let url = BASE + API_PREFIX + path;
  if (opts.query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.query)) if (v !== undefined && v !== '') qs.set(k, String(v));
    const s = qs.toString();
    if (s) url += `?${s}`;
  }
  const headers: Record<string, string> = { Accept: 'application/json' };
  const csrf = api.getCsrfToken();
  if (method !== 'GET' && csrf) headers['X-CSRF-Token'] = csrf;
  let body: string | undefined;
  if (opts.body !== undefined) { body = JSON.stringify(opts.body); headers['Content-Type'] = 'application/json'; }
  let res: Response;
  try {
    res = await fetch(url, { method, headers, body, credentials: 'include' });
  } catch (e) {
    console.error('network error', e);
    throw new ApiError(0, 'NETWORK', 'Cannot reach the server. Check your connection and try again.');
  }
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event(UNAUTH_EVENT));
    const text = await res.text();
    let raw: unknown = null;
    try { raw = text ? JSON.parse(text) : null; } catch (e) { console.warn('error body is not JSON', e); }
    const env = ErrorEnvelope.safeParse(raw);
    if (env.success) throw new ApiError(res.status, env.data.code, env.data.message, env.data.law, env.data.details, env.data.requestId);
    throw new ApiError(res.status, res.status >= 500 ? 'INTERNAL' : 'BAD_REQUEST', `Request failed (HTTP ${res.status}).`);
  }
  return res;
}

export async function plCall<K extends ProductsLawsRouteName>(name: K, opts: PlCallOptions<K> = {}): Promise<Out<K>> {
  const route = productsLawsRoutes[name];
  const res = await send(route.method, buildPath(route.path, opts.params), { query: opts.query, body: opts.body });
  let json: unknown;
  try { json = await res.json(); } catch (e) {
    console.error('bad json', e);
    throw new ApiError(res.status, 'BAD_RESPONSE', 'The server sent a response that is not JSON.');
  }
  const parsed = (route.response as z.ZodTypeAny).safeParse(json);
  if (!parsed.success) throw new ApiError(res.status, 'BAD_RESPONSE', `The server response for ${name} did not match the contract.`);
  return parsed.data as Out<K>;
}

/** GET a plain-text export and hand it to the browser as a file download. */
export async function plDownloadText(path: string, query: Record<string, string>, fallbackName: string): Promise<void> {
  const res = await send('GET', path, { query });
  const text = await res.text();
  const cd = res.headers.get('content-disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(cd)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** Read a small text file (txt, md, csv) in the browser. Other types are refused with a plain reason. */
export async function readTextFile(file: File): Promise<string> {
  if (!/\.(txt|md|csv|tsv)$/i.test(file.name) && !file.type.startsWith('text/')) {
    throw new Error('Only .txt, .md or .csv files can be read here. For a PDF or Word file, copy its text and paste it.');
  }
  if (file.size > 2 * 1024 * 1024) throw new Error('That file is larger than 2 MB. Paste the important part instead.');
  return file.text();
}

import { useOneArea } from '../../lib/area.js';

export interface PlArea { id: string; key: string; label: string }

/** The business area these pages work on: always the top-bar switcher's area (see lib/area.tsx). */
export function usePlArea(): { area: PlArea | null; areas: PlArea[]; isAreaAdmin: boolean } {
  const { area, areas, isAreaAdmin } = useOneArea();
  return { area, areas, isAreaAdmin };
}
